import {useMemo} from 'react';
import type {
  FeatureFlagEnum,
  RoleEnum,
  UserGroupModulePermissionResponse,
} from 'src/api/ProactApiClient';
import {PermissionOperationEnum, UserStatusEnum} from 'src/api/ProactApiClient';
import type {SidebarLinkItemProps} from 'src/app/layout/Sidebar/components/SidebarLinks';
import {useAppSelector} from 'src/redux/hooks';
import {findModulePermission} from 'src/shared/auth/useHasModulePermission';
import {usePermissions} from 'src/shared/auth/usePermissions';
import useActiveFeatureFlags from 'src/shared/hooks/useActiveFeatureFlags';

export interface SidebarLinkVisibilityContext {
  isSuperAdmin: boolean;
  roleId?: RoleEnum;
  userStatus?: UserStatusEnum;
  activeFeatureFlags?: FeatureFlagEnum[];
  permissions?: UserGroupModulePermissionResponse[];
}

// Mirrors the guard chain SidebarLinkItem renders (role, pending status,
// feature flag, module permission) as a plain predicate for list filtering.
export const isSidebarLinkVisible = (
  link: SidebarLinkItemProps,
  ctx: SidebarLinkVisibilityContext
): boolean => {
  if (link.isDivider) return false;
  if (link.superAdminOnly && !ctx.isSuperAdmin) return false;

  if (
    link.roles &&
    !ctx.isSuperAdmin &&
    ctx.roleId &&
    !link.roles.some(role => role === ctx.roleId)
  ) {
    return false;
  }

  if (
    !ctx.isSuperAdmin &&
    !link.allowPendingUsers &&
    ctx.userStatus === UserStatusEnum.Pending
  ) {
    return false;
  }

  if (
    link.featureFlag !== undefined &&
    ctx.activeFeatureFlags?.includes(link.featureFlag) !== true
  ) {
    return false;
  }

  if (
    link.module &&
    !ctx.isSuperAdmin &&
    findModulePermission(
      ctx.permissions ?? [],
      link.module,
      link.operation ?? PermissionOperationEnum.View,
      link.operationName
    ) === undefined
  ) {
    return false;
  }

  return true;
};

export const useSidebarLinkVisibilityContext =
  (): SidebarLinkVisibilityContext => {
    const {session, isSuperAdmin} = useAppSelector(state => state.session);
    const activeFeatureFlags = useActiveFeatureFlags();
    const {permissions} = usePermissions();

    return useMemo(
      () => ({
        isSuperAdmin,
        roleId: session?.user?.roleId,
        userStatus: session?.userStatus,
        activeFeatureFlags,
        permissions,
      }),
      [session, isSuperAdmin, activeFeatureFlags, permissions]
    );
  };

export const useVisibleSidebarLinks = (
  links: SidebarLinkItemProps[]
): SidebarLinkItemProps[] => {
  const ctx = useSidebarLinkVisibilityContext();
  return useMemo(
    () => links.filter(link => isSidebarLinkVisible(link, ctx)),
    [links, ctx]
  );
};
